<template>
  <BfFlex
    gap="1.2rem"
    justify="center"
  >
    <div class="current-setup__item">
      <h2>{{ t('common.bike.saddle') }}</h2>

      <BfFlex
        v-if="selectedMeasurement"
        gap="0.8rem"
      >
        <div class="current-setup__value">
          <h3>{{ t('common.position.x') }}</h3>
          <div
            class="current-setup__numbers"
            v-if="
              !hasPositioningMotors ||
              (isCalibrated && isLatestMeasurementCalibrated)
            "
          >
            {{ saddleX }} {{ t('common.unit.mm') }}
            <span class="current-setup__difference"
              >({{ `${saddleXChange} ${t('common.unit.mm')}` }})</span
            >
          </div>
          <div
            class="current-setup__numbers current-setup__numbers--inactive"
            v-else
          >
            {{ t('common.notCalibrated') }}
          </div>
        </div>
        <div class="current-setup__value">
          <h3>{{ t('common.position.y') }}</h3>
          <div
            class="current-setup__numbers"
            v-if="
              !hasPositioningMotors ||
              (isCalibrated && isLatestMeasurementCalibrated)
            "
          >
            {{ saddleY }} {{ t('common.unit.mm') }}
            <span class="current-setup__difference"
              >({{ `${saddleYChange} ${t('common.unit.mm')}` }})</span
            >
          </div>
          <div
            class="current-setup__numbers current-setup__numbers--inactive"
            v-else
          >
            {{ t('common.notCalibrated') }}
          </div>
        </div>
      </BfFlex>
      <p>
        {{
          selectedMeasurement?.saddle.active
            ? `${selectedMeasurement?.saddle.active.SADDLE_BRAND} ${selectedMeasurement?.saddle.active.SADDLE_MODEL}`
            : '-'
        }}
      </p>
    </div>

    <div class="current-setup__item">
      <h2>{{ t('common.bike.handlebar') }}</h2>

      <BfFlex
        v-if="selectedMeasurement"
        gap="0.8rem"
      >
        <div class="current-setup__value">
          <h3>{{ t('common.position.x') }}</h3>
          <div
            class="current-setup__numbers"
            v-if="
              !hasPositioningMotors ||
              (isCalibrated && isLatestMeasurementCalibrated)
            "
          >
            {{ Math.abs(handlebarX) }} {{ t('common.unit.mm') }}
            <span class="current-setup__difference"
              >({{ `${handlebarXChange * -1} ${t('common.unit.mm')}` }})</span
            >
          </div>
          <div
            class="current-setup__numbers current-setup__numbers--inactive"
            v-else
          >
            {{ t('common.notCalibrated') }}
          </div>
        </div>
        <div class="current-setup__value">
          <h3>{{ t('common.position.y') }}</h3>
          <div
            class="current-setup__numbers"
            v-if="
              !hasPositioningMotors ||
              (isCalibrated && isLatestMeasurementCalibrated)
            "
          >
            {{ handlebarY }} {{ t('common.unit.mm') }}
            <span class="current-setup__difference"
              >({{ `${handlebarYChange} ${t('common.unit.mm')}` }})</span
            >
          </div>
          <div
            class="current-setup__numbers current-setup__numbers--inactive"
            v-else
          >
            {{ t('common.notCalibrated') }}
          </div>
        </div>
      </BfFlex>
      <p>
        {{
          selectedMeasurement?.handlebar.active
            ? `${selectedMeasurement?.handlebar.active.HANDLEBAR_BRAND} ${selectedMeasurement?.handlebar.active.HANDLEBAR_MODEL}`
            : '-'
        }}
      </p>
    </div>
  </BfFlex>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useMeasurementStore } from '@/store/measurementStore'
import { usePositionStore } from '@/store/positionStore'
import { usePreferenceStore } from '@/store/preferenceStore'
import { BfFlex } from '@finetic/bikefitting-ui'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const measurementStore = useMeasurementStore()
const positionStore = usePositionStore()
const preferenceStore = usePreferenceStore()
const { selectedMeasurement } = storeToRefs(measurementStore)
const {
  absolutePosition,
  positionChange,
  isCalibrated,
  isLatestMeasurementCalibrated,
} = storeToRefs(positionStore)
const { hasPositioningMotors } = storeToRefs(preferenceStore)

const handlebarX = computed(() => absolutePosition.value.handlebarX)
const handlebarY = computed(() => absolutePosition.value.handlebarY)
const saddleX = computed(() => absolutePosition.value.saddleX)
const saddleY = computed(() => absolutePosition.value.saddleY)

const handlebarXChange = computed(() => positionChange.value.handlebarX)
const handlebarYChange = computed(() => positionChange.value.handlebarY)
const saddleXChange = computed(() => positionChange.value.saddleX)
const saddleYChange = computed(() => positionChange.value.saddleY)

watch(
  handlebarX,
  () => (selectedMeasurement.value.handlebar.x = handlebarX.value),
)
watch(
  handlebarY,
  () => (selectedMeasurement.value.handlebar.y = handlebarY.value),
)
watch(saddleX, () => (selectedMeasurement.value.saddle.x = saddleX.value))
watch(saddleY, () => (selectedMeasurement.value.saddle.y = saddleY.value))
</script>

<style lang="scss">
.current-setup {
  &__item {
    background-color: var(--color-light);
    padding: 1.2rem;
    border-radius: 0.8rem;
    box-shadow: 0 0.2rem 0.4rem rgb(0 0 0 / 0.1);
    flex: 1 0 50%;

    h2 {
      font-size: 1.4rem;
      padding-left: 1.2rem;
      color: var(--color-dark-60);
    }

    p {
      margin: 0.8rem 0 0 1.2rem;
    }
  }

  &__value {
    flex: 1;
    background-color: var(--color-dark-2);
    border-radius: 0.4rem;
    padding: 1.2rem;

    h3 {
      font-size: 1.2rem;
      margin-bottom: 0;
    }
  }

  &__numbers {
    flex: 1 0 50%;
    font-size: 1.8rem;
    color: var(--color-primary);

    &--inactive {
      color: var(--color-dark-30);
    }
  }

  &__difference {
    font-weight: 300;
    color: var(--color-dark-40);
  }
}
</style>
